/**
 * 지원 언어(로케일) 정의. SRS-WP-LANGUAGE: 한국어·영어만 지원한다.
 * 화면 문자열 사전(`./messages/*`)과 로케일 저장소(`./locale-store`)가 공통으로 참조한다.
 */
export const APP_LOCALES = ["ko", "en"] as const;

export type AppLocale = (typeof APP_LOCALES)[number];

/** 지역 감지에 실패했을 때 사용하는 기본 언어 */
export const DEFAULT_LOCALE: AppLocale = "ko";

/** 사용자가 선택한 언어를 보관하는 localStorage 키 */
export const LOCALE_STORAGE_KEY = "sat014.locale";

/** 언어 선택 메뉴에 노출하는 각 언어의 자국어 표기 */
export const localeNativeLabels: Record<AppLocale, string> = {
  ko: "한국어",
  en: "English",
};

/** `<html lang>` 및 Intl 포맷터에 사용하는 BCP 47 태그 */
export const localeLanguageTags: Record<AppLocale, string> = {
  ko: "ko-KR",
  en: "en-US",
};

/** 지역(국가) 코드만 확인 가능한 경우 사용하는 지역 → 언어 매핑 */
export const regionLocaleMap: Record<string, AppLocale> = {
  KR: "ko",
};

/** IANA 타임존 → 언어 매핑. navigator 언어 정보를 얻지 못한 환경의 보조 수단이다. */
export const timeZoneLocaleMap: Record<string, AppLocale> = {
  "Asia/Seoul": "ko",
};

export function isAppLocale(value: unknown): value is AppLocale {
  return typeof value === "string" && (APP_LOCALES as readonly string[]).includes(value);
}

/**
 * BCP 47 언어 태그에서 지원 언어를 추출한다.
 * 언어 서브태그가 먼저 확인되고, 지원하지 않는 언어면 지역 서브태그로 판단한다.
 * 예) "en" → en, "zh-KR" → ko(지역 기준), "fr-FR" → null
 */
export function localeFromLanguageTag(languageTag: string): AppLocale | null {
  const normalized = languageTag.trim();
  if (!normalized) return null;

  const [languageSubtag, ...restSubtags] = normalized.split("-");
  const language = languageSubtag.toLowerCase();
  if (isAppLocale(language)) return language;

  const region = restSubtags.find((subtag) => /^[A-Za-z]{2}$/.test(subtag));
  if (!region) return null;

  return regionLocaleMap[region.toUpperCase()] ?? null;
}
